import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { t, PROMPTS, EMOJIS } from './translations';
import { moderateMessage, getModerationError, checkRateLimit, checkRateLimitServer } from './moderation';
import JarSVG from './JarSVG';
import AdminPage from './AdminPage';
import OnboardingFlow, { shouldShowOnboarding, markOnboarded } from './OnboardingFlow';
import { sendGlowNotificationEmail } from './emailService';
import { getAIInspiration, getLocalInspiration } from './inspireAI';
import { getEarnedSticker, getNextSticker, getAllEarnedStickers } from './glowStickers';
import DailyJarPage from './DailyJar';
import { GlowCardModal } from './GlowCard';
import { PromptBanner } from './PromptedGlow';
import { LockedJarBanner } from './TimeLock';
import GlowWallPage from './GlowWall';
import { VoiceGlowPlayer, VoiceGlowSender } from './VoiceGlow';
import './index.css';
import PrivacyPage from './PrivacyPage';
import CookieBanner from './CookieBanner';

const MAX_GLOWS = 100;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function timeAgo(ts, lang) {
  const T = t[lang];
  const d = (Date.now() - new Date(ts).getTime()) / 1000;
  if (d < 60) return T.justNow;
  if (d < 3600) return `${Math.floor(d/60)}${T.mAgo}`;
  if (d < 86400) return `${Math.floor(d/3600)}${T.hAgo}`;
  return `${Math.floor(d/86400)}${T.dAgo}`;
}

// ─── GLITTER PARTICLES ────────────────────────────────────────────────────────
function Particles() {
  const ps = Array.from({length:10},(_,i)=>({id:i,size:40+Math.random()*90,left:Math.random()*100,delay:Math.random()*10,duration:12+Math.random()*14}));
  return (
    <div className="bg-particles">
      {ps.map(p=>(
        <div key={p.id} className="particle" style={{width:p.size,height:p.size,left:`${p.left}%`,animationDelay:`${p.delay}s`,animationDuration:`${p.duration}s`}}/>
      ))}
    </div>
  );
}

// ─── LOGO ICON ────────────────────────────────────────────────────────────────
function LogoIcon({size=38}) {
  return (
    <svg width={size} height={size} viewBox="0 0 680 400" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <radialGradient id="lH" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#1C1410"/>
          <stop offset="100%" stopColor="#0D0A14"/>
        </radialGradient>
        <linearGradient id="lJ" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFD166" stopOpacity="0.22"/>
          <stop offset="45%" stopColor="#FFE9A0" stopOpacity="0.16"/>
          <stop offset="100%" stopColor="#F4A500" stopOpacity="0.24"/>
        </linearGradient>
        <linearGradient id="lL" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#B8860B"/>
          <stop offset="100%" stopColor="#7A5800"/>
        </linearGradient>
        <linearGradient id="lLB" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFD166"/>
          <stop offset="100%" stopColor="#D4A017"/>
        </linearGradient>
        <linearGradient id="lnY2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFE580"/>
          <stop offset="100%" stopColor="#F4A500"/>
        </linearGradient>
        <linearGradient id="lnP2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFB3C6"/>
          <stop offset="100%" stopColor="#E8507A"/>
        </linearGradient>
        <linearGradient id="lnB2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7EECD8"/>
          <stop offset="100%" stopColor="#0F9E8A"/>
        </linearGradient>
        <linearGradient id="lnG2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#B9F6CA"/>
          <stop offset="100%" stopColor="#2E7D32"/>
        </linearGradient>
        <linearGradient id="lnV2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD166"/>
          <stop offset="100%" stopColor="#FF8FAB"/>
        </linearGradient>
        <clipPath id="lJC">
          <path d="M248 152 Q243 185 241 215 Q237 260 245 287 Q259 310 340 310 Q421 310 435 287 Q443 260 439 215 Q437 185 432 152 Z"/>
        </clipPath>
      </defs>
      <rect width="680" height="400" fill="url(#lH)" rx="16"/>
      <ellipse cx="340" cy="220" rx="155" ry="140" fill="#FFD166" opacity="0.06"/>
      <path d="M248 152 Q243 185 241 215 Q237 260 245 287 Q259 310 340 310 Q421 310 435 287 Q443 260 439 215 Q437 185 432 152 Z" fill="url(#lJ)" stroke="#FFD166" strokeWidth="2"/>
      <g clipPath="url(#lJC)">
        <g transform="translate(256,252) rotate(-20)"><rect width="60" height="40" rx="3" fill="url(#lnY2)" stroke="#F4A500" strokeWidth="1"/><polygon points="45,0 60,0 60,14" fill="#92400E" opacity="0.4"/></g>
        <g transform="translate(363,246) rotate(17)"><rect width="62" height="40" rx="3" fill="url(#lnP2)" stroke="#E8507A" strokeWidth="1"/><polygon points="46,0 62,0 62,14" fill="#831843" opacity="0.35"/></g>
        <g transform="translate(246,205) rotate(-11)"><rect width="58" height="38" rx="3" fill="url(#lnB2)" stroke="#0F9E8A" strokeWidth="1"/><polygon points="43,0 58,0 58,13" fill="#065E55" opacity="0.35"/></g>
        <g transform="translate(315,195) rotate(6)"><rect width="66" height="42" rx="3" fill="url(#lnG2)" stroke="#2E7D32" strokeWidth="1"/><polygon points="49,0 66,0 66,14" fill="#1B5E20" opacity="0.35"/></g>
        <g transform="translate(382,200) rotate(21)"><rect width="56" height="38" rx="3" fill="url(#lnV2)" stroke="#E8507A" strokeWidth="1"/><polygon points="42,0 56,0 56,13" fill="#831843" opacity="0.32"/></g>
        <g transform="translate(270,134) rotate(-19)"><rect width="52" height="32" rx="3" fill="url(#lnY2)" stroke="#F4A500" strokeWidth="1" opacity="0.9"/></g>
        <g transform="translate(364,128) rotate(14)"><rect width="54" height="32" rx="3" fill="url(#lnP2)" stroke="#E8507A" strokeWidth="1" opacity="0.88"/></g>
        <ellipse cx="340" cy="298" rx="72" ry="13" fill="#FFD166" opacity="0.22"/>
      </g>
      {/* Sparkle stars on the logo */}
      <g>
        <line x1="210" y1="180" x2="210" y2="194" stroke="#FFE580" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
        <line x1="203" y1="187" x2="217" y2="187" stroke="#FFE580" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
      </g>
      <g>
        <line x1="475" y1="200" x2="475" y2="212" stroke="#2DD4BF" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>
        <line x1="469" y1="206" x2="481" y2="206" stroke="#2DD4BF" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>
      </g>
      <g>
        <line x1="220" y1="300" x2="220" y2="310" stroke="#FF8FAB" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
        <line x1="215" y1="305" x2="225" y2="305" stroke="#FF8FAB" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      </g>
      <path d="M253 160 Q249 192 248 224" stroke="white" strokeWidth="7" strokeLinecap="round" fill="none" clipPath="url(#lJC)" opacity="0.14"/>
      <path d="M248 152 Q243 185 241 215 Q237 260 245 287 Q259 310 340 310 Q421 310 435 287 Q443 260 439 215 Q437 185 432 152 Z" fill="none" stroke="#FFD166" strokeWidth="1.8" opacity="0.55"/>
      <path d="M270 128 L270 152 L410 152 L410 128 Z" fill="#2B1C0E" stroke="#7A5800" strokeWidth="1.5"/>
      <rect x="260" y="102" width="160" height="22" rx="5" fill="url(#lL)" stroke="#5A4000" strokeWidth="1.5"/>
      <rect x="252" y="92" width="176" height="14" rx="5" fill="url(#lLB)" stroke="#B8860B" strokeWidth="1.5"/>
      <ellipse cx="340" cy="92" rx="17" ry="6" fill="#7A5800" stroke="#5A4000" strokeWidth="1.2"/>
    </svg>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  return (
    <nav dir={T.dir}>
      <div className="nav-brand" onClick={()=>navigate('/')}>
        <LogoIcon size={40}/>
        <div>
          <div className="logo" style={{fontFamily:T.displayFont}}>MyGlow<span className="logo-dot">.me</span></div>
          <span className="logo-sub">{T.logoSub}</span>
        </div>
      </div>
      <div className="nav-right">
        <button className="lang-btn" onClick={()=>setLang(l=>l==='en'?'ar':'en')}>
          {lang==='en'?'عربي':'EN'}
        </button>
        {user && (<>
          <button className="btn btn-ghost" style={{fontSize:'0.8rem',padding:'8px 14px'}} onClick={()=>navigate('/search')}>
            {lang==='en'?'🔍 Find':'🔍 ابحث'}
          </button>
          <button className="btn btn-ghost" style={{fontSize:'0.8rem',padding:'8px 10px'}} onClick={()=>navigate('/daily')} title={lang==='en'?'Daily glow':'ضوي اليومي'}>
            🌅
          </button>
          <button className="btn btn-ghost" style={{fontSize:'0.8rem',padding:'8px 14px'}} onClick={()=>navigate('/inbox')}>
            {T.myJar}
          </button>
          <button className="btn btn-ghost" style={{fontSize:'0.8rem',padding:'8px 14px'}} onClick={()=>navigate('/profile')}>
            {lang==='en'?'Profile':'بروفايل'}
          </button>
          <button className="btn btn-ghost" style={{fontSize:'0.8rem',padding:'8px 14px'}} onClick={onLogout}>
            {T.logOut}
          </button>
        </>)}
      </div>
    </nav>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
function HomePage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  useEffect(()=>{ if(user) navigate('/inbox'); },[user,navigate]);

  const notes = [
    {emoji:'💛', text: lang==='ar'?'"أنت بتضوّي أي مكان بس بوجودك"':'"You make every room brighter just by walking in"', color:'rgba(255,209,102,0.11)', border:'rgba(255,209,102,0.28)', rot:'-2deg'},
    {emoji:'🌸', text: lang==='ar'?'"فخور بيك أوي، أنت مش عارف قد إيه"':'"I\'m so proud of you, more than you know"', color:'rgba(255,143,171,0.11)', border:'rgba(255,143,171,0.3)', rot:'1.5deg'},
    {emoji:'✨', text: lang==='ar'?'"الدنيا أحسن بكتير عشان إنت فيها"':'"The world is genuinely better because you exist"', color:'rgba(45,212,191,0.1)', border:'rgba(45,212,191,0.26)', rot:'-1deg'},
  ];

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="hero-section">
          <div className="hero-left">
            <div className="hero-badge">✦ {T.freeHint}</div>
            <h1 style={{fontFamily:T.displayFont}}>
              {T.tagline}<br/>
              <span className="gold">{T.taglineGold}</span><br/>
              {T.taglineEnd}
            </h1>
            <p className="subtitle">{T.subtitle}</p>
            <div className="hero-btns">
              <button className="btn btn-glow" onClick={()=>navigate('/signup')}>{T.createAccount}</button>
              <button className="btn btn-ghost" onClick={()=>navigate('/login')}>{T.logIn}</button>
            </div>
          </div>
          <div className="hero-right">
            <div className="big-jar-wrap">
              <div className="big-jar-ring"/>
              <div className="big-jar-ring2"/>
              <JarSVG fill={0.65} size={220}/>
              <div className="float-note fn1">💌</div>
              <div className="float-note fn2">✨</div>
              <div className="float-note fn3">🌸</div>
              <div className="float-note fn4">💛</div>
            </div>
          </div>
        </div>

        <div className="notes-section">
          <div className="section-label">✦ {lang==='ar'?'أمثلة من الأضواء اللي بتتبعت':'What glows look like'}</div>
          <div className="notes-grid">
            {notes.map((n,i)=>(
              <div key={i} className="sample-note" style={{background:n.color, border:`1px solid ${n.border}`, transform:`rotate(${n.rot})`}}>
                <div className="sample-note-emoji">{n.emoji}</div>
                <div className="sample-note-text" style={{fontFamily:T.displayFont}}>{n.text}</div>
                <div className="sample-note-from">{lang==='ar'?'من صاحب مجهول ✦':'from an anonymous friend ✦'}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="how-section">
          <h2 className="section-title" style={{fontFamily:T.displayFont}}>{T.howTitle}</h2>
          <div className="how-grid">
            {[
              {num:'01',icon:'🫙',title:T.how1Title,sub:T.how1Sub},
              {num:'02',icon:'🔗',title:T.how2Title,sub:T.how2Sub},
              {num:'03',icon:'💛',title:T.how3Title,sub:T.how3Sub},
            ].map((s,i)=>(
              <div key={i} className="how-card">
                <div className="how-num">{s.num}</div>
                <div className="how-icon">{s.icon}</div>
                <div className="how-title" style={{fontFamily:T.displayFont}}>{s.title}</div>
                <div className="how-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="why-section">
          <h2 className="section-title" style={{fontFamily:T.displayFont}}>{T.whyTitle}</h2>
          <div className="why-grid">
            {[{icon:T.why1,sub:T.why1sub},{icon:T.why2,sub:T.why2sub},{icon:T.why3,sub:T.why3sub}].map((w,i)=>(
              <div key={i} className="why-card">
                <div className="why-icon">{w.icon}</div>
                <div className="why-sub">{w.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <div className="cta-jar"><JarSVG fill={0.8} size={100}/></div>
          <h2 style={{fontFamily:T.displayFont,fontSize:'clamp(1.6rem,4vw,2.4rem)',marginBottom:14}}>
            {lang==='ar'?'جرّتك بتستناك 🫙':'Your jar is waiting 🫙'}
          </h2>
          <p style={{color:'var(--text-muted)',marginBottom:28,maxWidth:380,lineHeight:1.7}}>{T.subtitle}</p>
          <button className="btn btn-glow" style={{fontSize:'1rem',padding:'15px 36px'}} onClick={()=>navigate('/signup')}>{T.createAccount}</button>
        </div>

        <footer className="site-footer">
          <span>MyGlow<span style={{color:'var(--gold)'}}>.</span>me</span>
          <div style={{display:'flex',gap:16,alignItems:'center',flexWrap:'wrap',justifyContent:'center'}}>
            <button onClick={()=>navigate('/glow-wall')} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:'0.78rem',cursor:'pointer',fontFamily:'inherit'}}>
              🌍 {lang==='ar'?'جدار الأضواء':'Glow Wall'}
            </button>
            <button onClick={()=>navigate('/privacy')} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:'0.78rem',cursor:'pointer',fontFamily:'inherit'}}>
              🔒 {lang==='ar'?'الخصوصية':'Privacy'}
            </button>
            <span style={{color:'var(--text-muted)',fontSize:'0.75rem'}}>
              {lang==='ar'?'صُنع بـ 💛 عشان كل واحد يحس بقيمته':'Made with 💛 to remind you of your worth'}
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}

// ─── AUTH PAGES ───────────────────────────────────────────────────────────────
function SignupPage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  const [name,setName]=useState('');const [email,setEmail]=useState('');const [password,setPassword]=useState('');
  const [loading,setLoading]=useState(false);const [error,setError]=useState('');
  useEffect(()=>{ if(user) navigate('/inbox'); },[user,navigate]);
  async function handleSignup(e) {
    e.preventDefault();
    if(!name.trim()||!email.trim()||!password.trim()) return;
    if(password.length<8){setError(T.weakPassword);return;}
    setLoading(true);setError('');
    const {error:err}=await supabase.auth.signUp({email:email.trim(),password,options:{data:{name:name.trim()}}});
    if(err){setError(err.message.includes('already')?T.emailInUse:T.error);}
    else navigate('/inbox');
    setLoading(false);
  }
  return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="auth-page"><div className="auth-card"><div className="auth-logo"><LogoIcon size={52}/></div><div className="auth-title" style={{fontFamily:T.displayFont,textAlign:'center'}}>{T.signUp}</div><div className="auth-sub" style={{textAlign:'center'}}>{T.subtitle.split('.')[0]}.</div><form onSubmit={handleSignup}><div className="form-group"><label className="form-label">{T.name}</label><input type="text" placeholder={T.namePlaceholder} value={name} onChange={e=>setName(e.target.value)} maxLength={30} required style={{textAlign:T.dir==='rtl'?'right':'left'}}/></div><div className="form-group"><label className="form-label">{T.email}</label><input type="email" placeholder={T.emailPlaceholder} value={email} onChange={e=>setEmail(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div><div className="form-group"><label className="form-label">{T.password}</label><input type="password" placeholder={T.passwordPlaceholder} value={password} onChange={e=>setPassword(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div>{error&&<div className="form-error">{error}</div>}<button className="btn btn-glow btn-full" type="submit" disabled={loading} style={{marginTop:8}}>{loading?'…':T.createAccount}</button></form><div className="auth-switch">{T.hasAccount}{' '}<button className="btn-link" onClick={()=>navigate('/login')}>{T.logIn}</button></div></div></div></div></>);
}

function LoginPage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  const [email,setEmail]=useState('');const [password,setPassword]=useState('');
  const [loading,setLoading]=useState(false);const [error,setError]=useState('');
  const [resetMode,setResetMode]=useState(false);const [resetSent,setResetSent]=useState(false);
  useEffect(()=>{ if(user) navigate('/inbox'); },[user,navigate]);
  async function handleLogin(e){e.preventDefault();setLoading(true);setError('');const{error:err}=await supabase.auth.signInWithPassword({email:email.trim(),password});if(err)setError(T.authError);else navigate('/inbox');setLoading(false);}
  async function handleReset(e){e.preventDefault();setLoading(true);await supabase.auth.resetPasswordForEmail(email.trim(),{redirectTo:'https://myglow.me/reset'});setResetSent(true);setLoading(false);}
  return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="auth-page"><div className="auth-card"><div className="auth-logo"><LogoIcon size={52}/></div><div className="auth-title" style={{fontFamily:T.displayFont,textAlign:'center'}}>{resetMode?T.forgotPassword:T.logIn}</div>{resetSent?(<div className="form-success" style={{textAlign:'center',marginTop:8}}>{T.resetSent}</div>):(<form onSubmit={resetMode?handleReset:handleLogin}><div className="form-group" style={{marginTop:16}}><label className="form-label">{T.email}</label><input type="email" placeholder={T.emailPlaceholder} value={email} onChange={e=>setEmail(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div>{!resetMode&&(<div className="form-group"><label className="form-label">{T.password}</label><input type="password" placeholder={T.passwordPlaceholder} value={password} onChange={e=>setPassword(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div>)}{error&&<div className="form-error">{error}</div>}<button className="btn btn-glow btn-full" type="submit" disabled={loading} style={{marginTop:8}}>{loading?'…':resetMode?T.resetPassword:T.loginBtn}</button>{!resetMode&&(<div style={{textAlign:'center',marginTop:12}}><button type="button" className="btn-link" onClick={()=>setResetMode(true)}>{T.forgotPassword}</button></div>)}{resetMode&&(<div style={{textAlign:'center',marginTop:12}}><button type="button" className="btn-link" onClick={()=>setResetMode(false)}>← {T.logIn}</button></div>)}</form>)}<div className="auth-switch">{T.noAccount}{' '}<button className="btn-link" onClick={()=>navigate('/signup')}>{T.signUp}</button></div></div></div></div></>);
}

// ─── MESSAGE MODAL ────────────────────────────────────────────────────────────
function MessageModal({msg, lang, onClose, onFavorite, onDelete, onReport, onShare, onTogglePublic}) {
  const T = t[lang];
  useEffect(()=>{
    function h(e){if(e.key==='Escape')onClose();}
    window.addEventListener('keydown',h);
    return()=>window.removeEventListener('keydown',h);
  },[onClose]);
  if(!msg) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e=>e.stopPropagation()} dir={T.dir}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-emoji">{msg.emoji}</div>
        <div className="modal-text" style={{fontFamily:T.displayFont}}>"{msg.text}"</div>
        <div className="modal-meta"><div className="anon-dot"/>{T.anonGlow} · {timeAgo(msg.created_at,lang)}</div>
        {msg.voice_type==='tts' && <VoiceGlowPlayer text={msg.text} emoji={msg.emoji} lang={lang}/>}
        <div className="modal-actions">
          <button className={`modal-action-btn${msg.favorited?' active-fav':''}`} onClick={()=>onFavorite(msg.id)}>
            {msg.favorited?'⭐ ':'☆ '}{lang==='ar'?'مفضلة':'Favorite'}
          </button>
          {!msg.reported&&(
            <button className="modal-action-btn" onClick={()=>{onReport(msg.id);onClose();}}>
              ⚑ {lang==='ar'?'بلّغ':'Report'}
            </button>
          )}
          <button
            className={`modal-action-btn${msg.public?' active-wall':''}`}
            onClick={()=>onTogglePublic(msg.id)}
            title={lang==='ar'?'شارك على جدار الأضواء':'Share to Glow Wall'}
          >
            {msg.public
              ? (lang==='ar'?'🌍 على الجدار':'🌍 On wall')
              : (lang==='ar'?'🌍 جدار الأضواء':'🌍 Glow Wall')}
          </button>
          <button className="modal-action-btn" onClick={()=>onShare(msg)}>
            🖼 {lang==='ar'?'شارك':'Share card'}
          </button>
          <button className="modal-action-btn danger" onClick={()=>{onDelete(msg.id);onClose();}}>
            🗑 {lang==='ar'?'حذف':'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── INBOX ────────────────────────────────────────────────────────────────────
function InboxPage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  const [profile,setProfile]=useState(null);
  const [messages,setMessages]=useState([]);
  const [loading,setLoading]=useState(true);
  const [copied,setCopied]=useState(false);
  const [activeTab,setActiveTab]=useState('all');
  const [search,setSearch]=useState('');
  const [selectedMsg,setSelectedMsg]=useState(null);
  const [showOnboarding,setShowOnboarding]=useState(false);
  const [newStickerToast,setNewStickerToast]=useState(null);
  const [glowCardMsg,setGlowCardMsg]=useState(null);

  useEffect(()=>{ if(!user) navigate('/login'); },[user,navigate]);
  useEffect(()=>{ if(user && shouldShowOnboarding()) setShowOnboarding(true); },[user]);

  useEffect(()=>{
    if(!user) return;
    async function load() {
      setLoading(true);
      const {data:prof}=await supabase.from('profiles').select('*').eq('id',user.id).single();
      setProfile(prof);
      if(prof){
        const {data:msgs}=await supabase.from('messages').select('*').eq('jar_id',prof.jar_id).order('created_at',{ascending:false});
        setMessages(msgs||[]);
      }
      setLoading(false);
    }
    load();
    const sub=supabase.channel('inbox')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'messages'},
        payload=>setMessages(prev=>[payload.new,...prev]))
      .subscribe();
    return()=>supabase.removeChannel(sub);
  },[user]);

  async function markRead(id){
    await supabase.from('messages').update({read:true}).eq('id',id);
    setMessages(prev=>prev.map(m=>m.id===id?{...m,read:true}:m));
  }
  async function toggleFavorite(id){
    const msg=messages.find(m=>m.id===id);if(!msg)return;
    const newVal=!msg.favorited;
    await supabase.from('messages').update({favorited:newVal}).eq('id',id);
    setMessages(prev=>prev.map(m=>m.id===id?{...m,favorited:newVal}:m));
  }
  async function deleteMessage(id){
    await supabase.from('messages').delete().eq('id',id);
    setMessages(prev=>prev.filter(m=>m.id!==id));
  }
  async function reportMessage(id){
    const msg=messages.find(m=>m.id===id);if(!msg)return;
    await supabase.from('messages').update({reported:true,report_count:(msg.report_count||0)+1}).eq('id',id);
    setMessages(prev=>prev.map(m=>m.id===id?{...m,reported:true}:m));
  }
  async function togglePublic(id){
    const msg=messages.find(m=>m.id===id);if(!msg)return;
    const newVal=!msg.public;
    await supabase.from('messages').update({public:newVal}).eq('id',id);
    setMessages(prev=>prev.map(m=>m.id===id?{...m,public:newVal}:m));
  }
  function openMsg(msg){markRead(msg.id);setSelectedMsg(msg);}
  useEffect(()=>{
    if(selectedMsg){const updated=messages.find(m=>m.id===selectedMsg.id);if(updated)setSelectedMsg(updated);}
  },[messages]);
  function copyLink(){
    if(!profile)return;
    navigator.clipboard.writeText(`https://myglow.me/${profile.jar_id}`).catch(()=>{});
    setCopied(true);setTimeout(()=>setCopied(false),2200);
  }

  const fillLevel=Math.min((messages.length||0)/MAX_GLOWS,1);
  const unread=messages.filter(m=>!m.read).length;
  const filtered=messages
    .filter(m=>{if(activeTab==='favorites')return m.favorited;if(activeTab==='reported')return m.reported;return true;})
    .filter(m=>search.trim()===''||m.text.toLowerCase().includes(search.toLowerCase()));
  const tabs=[
    {id:'all',label:lang==='ar'?'الكل':'All',count:messages.length},
    {id:'favorites',label:lang==='ar'?'⭐ مفضلة':'⭐ Favorites',count:messages.filter(m=>m.favorited).length},
    {id:'reported',label:lang==='ar'?'⚑ بلّغ عنها':'⚑ Reported',count:messages.filter(m=>m.reported).length},
  ];

  if(loading) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">✨</div><div className="center-sub">{T.loading}</div></div></div></>);

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="inbox">
          <div className="inbox-header">
            <div>
              <h2 style={{fontFamily:T.displayFont}}>{T.dir==='rtl'?T.jarTitle+(profile?.name||''):((profile?.name||'')+T.jarTitle)}</h2>
              {unread>0&&<div className="glow-count">✨ {unread} {unread===1?T.newGlows:T.newGlowsPlural}</div>}
              {unread===0&&messages.length>0&&<div className="glow-count" style={{opacity:0.55}}>{T.allCaughtUp}</div>}
            </div>
            <button className="btn btn-glow" style={{fontSize:'0.85rem',padding:'10px 18px'}} onClick={()=>navigate('/send/'+profile?.jar_id)}>{T.sendGlow}</button>
          </div>

          <div className="jar-progress">
            <div style={{flexShrink:0}}><JarSVG fill={fillLevel} size={58}/></div>
            <div className="jar-info">
              <div className="jar-info-title">{messages.length===0?T.jarEmpty:`${Math.round(fillLevel*100)}${T.jarFull}`}</div>
              <div className="jar-bar-bg"><div className="jar-bar-fill" style={{width:`${fillLevel*100}%`}}/></div>
              <div className="jar-bar-label">{messages.length} {T.glowsCollected} · {lang==='ar'?'بتتملى لحد ١٠٠':'fills up to 100'}</div>
              {/* Sticker display */}
              {(()=>{
                const sticker = getEarnedSticker(messages.length);
                const next = getNextSticker(messages.length);
                if(!sticker && !next) return null;
                return (
                  <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    {sticker && (
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,background:'rgba(255,209,102,0.12)',border:'1px solid rgba(255,209,102,0.25)',borderRadius:100,padding:'3px 10px',fontSize:'0.75rem',color:'var(--gold)'}}>
                        {sticker.emoji} {lang==='ar'?sticker.ar:sticker.en}
                      </span>
                    )}
                    {next && (
                      <span style={{fontSize:'0.7rem',color:'var(--text-muted)'}}>
                        {lang==='ar'?`${next.threshold - messages.length} ضو للـ ${next.ar}`:`${next.threshold - messages.length} more to earn ${next.emoji} ${next.en}`}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="share-strip">
            <div>
              <div className="share-text">{T.shareLink}</div>
              <div className="share-link">myglow.me/{profile?.jar_id}</div>
            </div>
            <button className={`copy-btn${copied?' copied':''}`} onClick={copyLink}>{copied?T.copied:T.copyLink}</button>
          </div>

          {/* Time-lock banner */}
          <LockedJarBanner unlockAt={profile?.unlock_at} messageCount={messages.length} lang={lang}/>

          {/* Daily Jar shortcut */}
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <button className="btn btn-ghost" style={{fontSize:'0.82rem',padding:'9px 16px',flex:1}} onClick={()=>navigate('/daily')}>
              🌅 {lang==='ar'?'ضوي اليومي':'Daily glow'}
            </button>
            <button className="btn btn-ghost" style={{fontSize:'0.82rem',padding:'9px 16px',flex:1}} onClick={()=>navigate('/glow-wall')}>
              🌍 {lang==='ar'?'جدار الأضواء':'Glow wall'}
            </button>
          </div>

          {messages.length>0&&(
            <div className="inbox-controls">
              <div className="inbox-tabs">
                {tabs.map(tab=>(
                  <button key={tab.id} className={`inbox-tab${activeTab===tab.id?' active':''}`} onClick={()=>setActiveTab(tab.id)}>
                    {tab.label}{tab.count>0&&<span className="tab-count">{tab.count}</span>}
                  </button>
                ))}
              </div>
              <div className="inbox-search-wrap">
                <span className="inbox-search-icon">🔍</span>
                <input type="text" className="inbox-search" placeholder={lang==='ar'?'ابحث في الأضواء…':'Search glows…'} value={search} onChange={e=>setSearch(e.target.value)} style={{textAlign:T.dir==='rtl'?'right':'left'}}/>
                {search&&<button className="inbox-search-clear" onClick={()=>setSearch('')}>✕</button>}
              </div>
            </div>
          )}

          {messages.length===0?(
            <div className="empty"><div className="empty-icon">🫙</div><div className="empty-title" style={{fontFamily:T.displayFont}}>{T.emptyJarTitle}</div><div className="empty-sub">{T.emptyJarSub}</div></div>
          ):filtered.length===0?(
            <div className="empty"><div className="empty-icon">🔍</div><div className="empty-title">{lang==='ar'?'مش لاقي حاجة':'Nothing found'}</div><div className="empty-sub">{lang==='ar'?'جرّب كلمة تانية':'Try a different search'}</div></div>
          ):(
            <div className="msg-grid">
              {filtered.map((msg,i)=>(
                <div key={msg.id} className={`msg-card${!msg.read?' unread':''}${msg.favorited?' favorited':''}`} style={{animationDelay:`${i*0.04}s`}} onClick={()=>openMsg(msg)}>
                  <div className="msg-glow-bg"/>
                  {!msg.read&&<div className="new-badge">NEW ✦</div>}
                  {msg.favorited&&<div className="fav-badge">⭐</div>}
                  <div className="msg-emoji">{msg.emoji}</div>
                  <div className="msg-text" style={{fontFamily:T.displayFont}}>"{msg.text}"</div>
                  <div className="msg-footer"><div className="anon-dot"/>{T.anonGlow} · {timeAgo(msg.created_at,lang)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedMsg&&<MessageModal msg={selectedMsg} lang={lang} onClose={()=>setSelectedMsg(null)} onFavorite={toggleFavorite} onDelete={deleteMessage} onReport={reportMessage} onTogglePublic={togglePublic} onShare={(m)=>{setGlowCardMsg(m);setSelectedMsg(null);}}/>}
      {glowCardMsg&&<GlowCardModal msg={glowCardMsg} lang={lang} onClose={()=>setGlowCardMsg(null)}/>}
      {showOnboarding&&<OnboardingFlow user={user} lang={lang} onDone={()=>setShowOnboarding(false)}/>}
      {newStickerToast&&(
        <div style={{position:'fixed',bottom:28,left:'50%',transform:'translateX(-50%)',zIndex:250,background:'var(--card)',border:'1px solid rgba(255,209,102,0.4)',borderRadius:16,padding:'16px 24px',boxShadow:'0 8px 40px rgba(0,0,0,0.6)',display:'flex',alignItems:'center',gap:12,animation:'fadeUp 0.4s ease',maxWidth:360,width:'90%'}} onClick={()=>setNewStickerToast(null)}>
          <span style={{fontSize:'2rem'}}>{newStickerToast.emoji}</span>
          <div>
            <div style={{fontWeight:600,fontSize:'0.9rem',color:'var(--gold)'}}>
              {lang==='ar'?'حصلت على ستيكر جديد!':'New sticker earned!'}
            </div>
            <div style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>
              {lang==='ar'?newStickerToast.ar:newStickerToast.en} · {lang==='ar'?newStickerToast.arSub:newStickerToast.enSub}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── SEND PAGE ────────────────────────────────────────────────────────────────
function SendPage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const {jarId} = useParams();
  const navigate = useNavigate();
  const [recipientName,setRecipientName]=useState('');
  const [notFound,setNotFound]=useState(false);
  const [isSelf,setIsSelf]=useState(false);
  const [draft,setDraft]=useState('');
  const [emoji,setEmoji]=useState('✨');
  const [sending,setSending]=useState(false);
  const [sent,setSent]=useState(false);
  const [moderationError,setModerationError]=useState('');
  const [inspiring,setInspiring]=useState(false);

  async function handleInspire() {
    setInspiring(true);
    const text = await getAIInspiration(recipientName, lang);
    setDraft(text);
    setInspiring(false);
  }

  useEffect(()=>{
    async function load(){
      const {data}=await supabase.from('profiles').select('id,name').eq('jar_id',jarId).single();
      if(!data){setNotFound(true);return;}
      setRecipientName(data.name);
      if(user&&user.id===data.id) setIsSelf(true);
    }
    load();
  },[jarId,user]);

  async function sendGlow(){
    if(!draft.trim()) return;
    // Client-side rate limit (immediate UI feedback)
    const sessionKey=(navigator.userAgent||'anon').slice(0,20);
    const rateCheck=checkRateLimit(jarId,sessionKey);
    if(!rateCheck.ok){setModerationError(getModerationError('rate_limit',lang));return;}
    // Content moderation
    const mod=moderateMessage(draft);
    if(!mod.ok){setModerationError(getModerationError(mod.reason,lang));return;}
    setModerationError('');setSending(true);
    // Server-side rate limit (Postgres-enforced, bypass-proof)
    const serverRate=await checkRateLimitServer(supabase,jarId);
    if(!serverRate.ok){setModerationError(getModerationError('rate_limit',lang));setSending(false);return;}
    const {error}=await supabase.from('messages').insert({jar_id:jarId,text:draft.trim(),emoji,read:false,reported:false,report_count:0,favorited:false});
    if(!error) {
      setSent(true);
      // Fire email notification (non-blocking, best-effort)
      try {
        const {data:ownerProfile}=await supabase.from('profiles').select('id').eq('jar_id',jarId).single();
        if(ownerProfile) {
          const {data:{user:ownerUser}}=await supabase.auth.admin?.getUserById?.(ownerProfile.id) || {data:{user:null}};
          // Email is sent server-side via edge function triggered by DB webhook
          // See supabase_v7_setup.md for webhook configuration
        }
      } catch {}
    }
    setSending(false);
  }

  if(sent) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">✨</div><div className="center-title" style={{fontFamily:T.displayFont}}>{T.glowSent}</div><p className="center-sub">{T.glowSentSub}</p><br/><br/><button className="btn btn-glow" onClick={()=>navigate(user?'/inbox':'/')}>{user?T.backToGlow:T.getOwn}</button></div></div></>);
  if(isSelf) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div style={{fontSize:'3.2rem',marginBottom:18}}>🫙</div><div className="center-title" style={{fontFamily:T.displayFont}}>{lang==='ar'?'دي جرّتك الخاصة!':'This is your own jar!'}</div><p className="center-sub">{lang==='ar'?'مش ممكن تبعت لنفسك. شارك اللينك مع صحابك.':'You can\'t send a glow to yourself. Share the link with friends!'}</p><br/><br/><button className="btn btn-glow" onClick={()=>navigate('/inbox')}>{T.backToGlow}</button></div></div></>);
  if(notFound) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div style={{fontSize:'3.2rem',marginBottom:18}}>🫙</div><div style={{fontSize:'1.4rem',fontWeight:700,marginBottom:10,color:'var(--text)'}}>{T.notFound}</div><p className="center-sub">{T.notFoundSub}</p><br/><br/><button className="btn btn-glow" onClick={()=>navigate('/')}>{T.getOwn}</button></div></div></>);

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="compose-wrap">
          <div style={{textAlign:'center',marginBottom:24}}>
            <JarSVG fill={0.3} size={88}/>
            <div style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:8}}>{T.addToJar}</div>
          </div>
          <div className="compose-card">
            <div className="to-badge">💌 <span className="to-name" style={{fontFamily:T.displayFont}}>{recipientName}</span></div>
            <div className="emoji-row">{EMOJIS.map(e=>(<button key={e} className={`emoji-btn${emoji===e?' active':''}`} onClick={()=>setEmoji(e)}>{e}</button>))}</div>
            <textarea placeholder={T.writePlaceholder} value={draft} onChange={e=>setDraft(e.target.value)} maxLength={500} style={{textAlign:T.dir==='rtl'?'right':'left'}}/>
            <div className="compose-footer">
              <div className="anon-note">🔒 {T.anonymous}</div>
              <span className="char-count">{draft.length}/500</span>
            </div>
            {moderationError&&<div style={{marginTop:10,padding:'10px 14px',background:'rgba(255,112,112,0.12)',border:'1px solid rgba(255,112,112,0.25)',borderRadius:10,color:'#FF8080',fontSize:'0.82rem'}}>⚠️ {moderationError}</div>}
            {/* Prompt banner (if jar owner set one) */}
            <PromptBanner prompt={null} lang={lang}/>
            {/* Voice glow sender */}
            <VoiceGlowSender text={draft} lang={lang} onSendAsVoice={(char)=>{
              // Store voice_char in message metadata — future enhancement
              sendGlow();
            }}/>
            <div className="compose-actions">
              <button className="inspire-btn" onClick={handleInspire} disabled={inspiring}>
                {inspiring ? (lang==='ar'?'✨ جاري التوليد…':'✨ Generating…') : `✦ ${T.inspireMe}`}
              </button>
              <button className="btn btn-glow" onClick={sendGlow} disabled={!draft.trim()||sending}>{sending?'…':T.sendBtn}</button>
            </div>
          </div>
          {!user&&<div style={{textAlign:'center',marginTop:20,fontSize:'0.82rem',color:'var(--text-muted)'}}>{T.getOwn2}{' '}<span style={{color:'var(--gold)',cursor:'pointer',fontWeight:500}} onClick={()=>navigate('/signup')}>{T.createFree}</span></div>}
        </div>
      </div>
    </>
  );
}

// ─── PUBLIC PROFILE PAGE ──────────────────────────────────────────────────────
function PublicProfilePage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const {username} = useParams();
  const navigate = useNavigate();
  const [profile,setProfile]=useState(null);
  const [notFound,setNotFound]=useState(false);
  const [loading,setLoading]=useState(true);

  const [glowCount,setGlowCount]=useState(0);

  useEffect(()=>{
    async function load(){
      const {data}=await supabase.from('profiles').select('*').eq('username',username).single();
      if(!data) setNotFound(true);
      else {
        setProfile(data);
        // Get glow count for sticker display
        const {count}=await supabase.from('messages').select('*',{count:'exact',head:true}).eq('jar_id',data.jar_id);
        setGlowCount(count||0);
      }
      setLoading(false);
    }
    load();
  },[username]);

  const getAvatarUrl=(profile)=>{
    if(!profile?.avatar_url) return null;
    const {data}=supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
    return data?.publicUrl||null;
  };

  if(loading) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">✨</div></div></div></>);
  if(notFound) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div style={{fontSize:'3rem',marginBottom:18}}>👤</div><div className="center-title">{lang==='ar'?'البروفايل ده مش موجود':'Profile not found'}</div><br/><button className="btn btn-glow" onClick={()=>navigate('/')}>{lang==='ar'?'الرئيسية':'Home'}</button></div></div></>);

  const avatarUrl=getAvatarUrl(profile);
  const socials=profile.social_links||{};
  const isSelf=user&&user.id===profile.id;

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="public-profile">
          <div className="profile-avatar">
            {avatarUrl?<img src={avatarUrl} alt={profile.name}/>:(profile.avatar_emoji||'✨')}
          </div>
          <div className="profile-name" style={{fontFamily:T.displayFont}}>{profile.name}</div>
          {profile.username&&<div className="profile-username">@{profile.username}</div>}
          {/* Sticker badge */}
          {(()=>{
            const sticker = getEarnedSticker(glowCount);
            if(!sticker) return null;
            return (
              <div style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(255,209,102,0.1)',border:'1px solid rgba(255,209,102,0.22)',borderRadius:100,padding:'4px 12px',fontSize:'0.78rem',color:'var(--gold)',marginBottom:12}}>
                {sticker.emoji} {lang==='ar'?sticker.ar:sticker.en}
              </div>
            );
          })()}
          {profile.bio&&<div className="profile-bio">{profile.bio}</div>}
          {Object.keys(socials).length>0&&(
            <div className="profile-socials">
              {socials.instagram&&<a href={`https://instagram.com/${socials.instagram}`} target="_blank" rel="noopener noreferrer" className="social-link">📸 Instagram</a>}
              {socials.twitter&&<a href={`https://twitter.com/${socials.twitter}`} target="_blank" rel="noopener noreferrer" className="social-link">🐦 Twitter</a>}
              {socials.tiktok&&<a href={`https://tiktok.com/@${socials.tiktok}`} target="_blank" rel="noopener noreferrer" className="social-link">🎵 TikTok</a>}
              {socials.linkedin&&<a href={`https://linkedin.com/in/${socials.linkedin}`} target="_blank" rel="noopener noreferrer" className="social-link">💼 LinkedIn</a>}
            </div>
          )}
          {isSelf?(
            <button className="btn btn-ghost" style={{marginTop:20}} onClick={()=>navigate('/profile')}>{lang==='ar'?'تعديل البروفايل':'Edit Profile'}</button>
          ):(
            <button className="btn btn-glow" style={{marginTop:20,fontSize:'1rem',padding:'14px 32px'}} onClick={()=>navigate('/send/'+profile.jar_id)}>
              ✨ {lang==='ar'?'ابعت ضو لـ':'Send a glow to'} {profile.name}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── INLINE PROFILE SETTING WRAPPERS ─────────────────────────────────────────
function PromptSettingsInline({profile, lang}) {
  const { PromptSettings } = require('./PromptedGlow');
  return <PromptSettings profile={profile} lang={lang}/>;
}
function TimeLockSettingsInline({profile, lang}) {
  const { TimeLockSettings } = require('./TimeLock');
  return <TimeLockSettings profile={profile} lang={lang}/>;
}

// ─── MY PROFILE EDIT ─────────────────────────────────────────────────────────
function MyProfilePage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  const [profile,setProfile]=useState(null);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const [uploadingAvatar,setUploadingAvatar]=useState(false);
  const [avatarPreview,setAvatarPreview]=useState(null);
  const fileInputRef=useRef(null);
  const [form,setForm]=useState({username:'',bio:'',avatar_emoji:'✨',instagram:'',twitter:'',tiktok:'',linkedin:''});
  const [error,setError]=useState('');

  useEffect(()=>{ if(!user) navigate('/login'); },[user,navigate]);
  useEffect(()=>{
    if(!user) return;
    async function load(){
      const {data}=await supabase.from('profiles').select('*').eq('id',user.id).single();
      setProfile(data);
      if(data){
        const s=data.social_links||{};
        setForm({username:data.username||'',bio:data.bio||'',avatar_emoji:data.avatar_emoji||'✨',instagram:s.instagram||'',twitter:s.twitter||'',tiktok:s.tiktok||'',linkedin:s.linkedin||''});
        if(data.avatar_url){
          const {data:urlData}=supabase.storage.from('avatars').getPublicUrl(data.avatar_url);
          if(urlData?.publicUrl) setAvatarPreview(urlData.publicUrl);
        }
      }
      setLoading(false);
    }
    load();
  },[user]);

  async function handleAvatarUpload(e) {
    const file=e.target.files?.[0];
    if(!file) return;
    if(file.size>3*1024*1024){setError(lang==='ar'?'الصورة أكبر من ٣ ميجا':'Image must be under 3MB');return;}
    if(!file.type.startsWith('image/')){setError(lang==='ar'?'لازم يكون صورة':'Please upload an image file');return;}
    setUploadingAvatar(true);setError('');
    // Show local preview immediately
    const reader=new FileReader();
    reader.onload=ev=>setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
    // Upload to Supabase Storage
    const ext=file.name.split('.').pop();
    const path=`${user.id}/avatar.${ext}`;
    const {error:upErr}=await supabase.storage.from('avatars').upload(path,file,{upsert:true,contentType:file.type});
    if(upErr){setError(T.error);setUploadingAvatar(false);return;}
    // Save path to profile
    await supabase.from('profiles').update({avatar_url:path}).eq('id',user.id);
    setUploadingAvatar(false);
  }

  async function handleSave(e){
    e.preventDefault();setError('');
    if(form.username&&!/^[a-zA-Z0-9_]{3,20}$/.test(form.username)){
      setError(lang==='ar'?'اسم المستخدم: حروف وأرقام وشرطة سفلية فقط (٣-٢٠ حرف)':'Username: letters, numbers, underscores only (3-20 chars)');
      return;
    }
    setSaving(true);
    const social_links={};
    if(form.instagram) social_links.instagram=form.instagram.replace('@','');
    if(form.twitter) social_links.twitter=form.twitter.replace('@','');
    if(form.tiktok) social_links.tiktok=form.tiktok.replace('@','');
    if(form.linkedin) social_links.linkedin=form.linkedin;
    const {error:err}=await supabase.from('profiles').update({username:form.username||null,bio:form.bio||null,avatar_emoji:form.avatar_emoji,social_links}).eq('id',user.id);
    if(err){setError(err.message.includes('unique')?(lang==='ar'?'اسم المستخدم ده مأخود':'Username taken, try another'):T.error);}
    else{setSaved(true);setTimeout(()=>setSaved(false),2500);}
    setSaving(false);
  }

  const AVATAR_EMOJIS=['✨','🌟','💛','🌸','🫂','🕊️','🌈','💌','🌻','🔥','🦋','🌙','⭐','🎯','🎨','🎵'];

  if(loading) return (<><Particles/><div className="page" dir={T.dir} style={{fontFamily:T.font}}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">✨</div></div></div></>);

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="profile-edit-wrap">
          <div className="profile-edit-card">
            <div className="profile-edit-header">
              <div className="profile-avatar" style={{fontSize:'2.2rem',width:72,height:72}}>
                {avatarPreview?<img src={avatarPreview} alt="avatar"/>:(form.avatar_emoji)}
              </div>
              <div>
                <div style={{fontFamily:T.displayFont,fontSize:'1.4rem',fontWeight:700}}>{profile?.name}</div>
                {form.username&&<div style={{color:'var(--text-muted)',fontSize:'0.85rem'}}>@{form.username}</div>}
              </div>
            </div>

            {profile?.username&&(
              <div className="profile-share-row">
                <span style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>{lang==='ar'?'بروفايلك العام:':'Your public profile:'}</span>
                <button className="btn-link" onClick={()=>navigate(`/u/${profile.username}`)}>myglow.me/u/{profile.username}</button>
              </div>
            )}

            <form onSubmit={handleSave}>
              {/* ── Profile Photo Upload ── */}
              <div className="form-group">
                <label className="form-label">{lang==='ar'?'📸 صورة البروفايل':'📸 Profile photo'}</label>
                <div className="avatar-upload-wrap">
                  <div className="avatar-upload-circle" onClick={()=>fileInputRef.current?.click()}>
                    {avatarPreview?(
                      <img src={avatarPreview} alt="preview"/>
                    ):(
                      <span>{uploadingAvatar?'⏳':form.avatar_emoji}</span>
                    )}
                    <div className="avatar-upload-overlay">
                      {uploadingAvatar?'⏳':'📷'}
                    </div>
                  </div>
                  <div className="avatar-upload-hint">
                    {lang==='ar'?'اضغط عشان ترفع صورة (حد أقصى ٣ ميجا)':'Click to upload a photo (max 3MB)'}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleAvatarUpload}/>
                </div>
              </div>

              {/* ── Emoji fallback picker (shown if no photo) ── */}
              {!avatarPreview&&(
                <div className="form-group">
                  <label className="form-label">{lang==='ar'?'أو اختار أيقونة':'Or pick an emoji instead'}</label>
                  <div className="avatar-picker">
                    {AVATAR_EMOJIS.map(em=>(
                      <button type="button" key={em} className={`avatar-opt${form.avatar_emoji===em?' active':''}`} onClick={()=>setForm(f=>({...f,avatar_emoji:em}))}>{em}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">{lang==='ar'?'اسم المستخدم (اختياري)':'Username (optional)'}</label>
                <input type="text" placeholder={lang==='ar'?'مثلاً: sara_glow':'e.g. sara_glow'} value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'')}))} maxLength={20} style={{textAlign:'left',direction:'ltr'}}/>
                <span style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{lang==='ar'?'هيظهر على بروفايلك العام':'Shown on your public profile at myglow.me/u/username'}</span>
              </div>

              <div className="form-group">
                <label className="form-label">{lang==='ar'?'نبذة عنك (اختياري)':'Bio (optional)'}</label>
                <textarea placeholder={lang==='ar'?'قول حاجة عن نفسك…':'Say something about yourself…'} value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} maxLength={160} style={{minHeight:80,textAlign:T.dir==='rtl'?'right':'left'}}/>
              </div>

              {/* Prompted glow settings */}
              {profile && <PromptSettingsInline profile={profile} lang={lang}/>}
              {/* Time-lock settings */}
              {profile && <TimeLockSettingsInline profile={profile} lang={lang}/>}

              <div className="profile-socials-section">
                <div className="form-label" style={{marginBottom:12}}>{lang==='ar'?'🔗 روابط التواصل (اختياري)':'🔗 Social links (optional)'}</div>
                {[
                  {key:'instagram',label:'📸 Instagram',placeholder:'@username'},
                  {key:'twitter',label:'🐦 Twitter / X',placeholder:'@username'},
                  {key:'tiktok',label:'🎵 TikTok',placeholder:'@username'},
                  {key:'linkedin',label:'💼 LinkedIn',placeholder:'profile-slug'},
                ].map(s=>(
                  <div className="form-group" key={s.key} style={{marginBottom:10}}>
                    <label className="form-label">{s.label}</label>
                    <input type="text" placeholder={s.placeholder} value={form[s.key]} onChange={e=>setForm(f=>({...f,[s.key]:e.target.value}))} style={{textAlign:'left',direction:'ltr'}}/>
                  </div>
                ))}
              </div>

              {error&&<div className="form-error" style={{marginBottom:10}}>{error}</div>}
              {saved&&<div className="form-success" style={{marginBottom:10}}>{lang==='ar'?'✓ اتحفظ':'✓ Saved!'}</div>}
              <button className="btn btn-glow btn-full" type="submit" disabled={saving}>{saving?'…':(lang==='ar'?'حفظ التغييرات':'Save changes')}</button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── PEOPLE SEARCH ────────────────────────────────────────────────────────────
function SearchPage({lang, setLang, user, onLogout}) {
  const T = t[lang];
  const navigate = useNavigate();
  const [query,setQuery]=useState('');
  const [results,setResults]=useState([]);
  const [loading,setLoading]=useState(false);
  const [searched,setSearched]=useState(false);
  const debounceRef=useRef(null);

  useEffect(()=>{ if(!user) navigate('/login'); },[user,navigate]);
  useEffect(()=>{
    if(debounceRef.current) clearTimeout(debounceRef.current);
    if(!query.trim()||query.trim().length<2){setResults([]);setSearched(false);return;}
    debounceRef.current=setTimeout(async()=>{
      setLoading(true);
      const {data}=await supabase.from('profiles').select('id,name,username,bio,avatar_emoji,avatar_url,jar_id')
        .or(`username.ilike.%${query}%,name.ilike.%${query}%`).limit(20);
      setResults(data||[]);setSearched(true);setLoading(false);
    },350);
  },[query]);

  function getAvatarUrl(p){
    if(!p.avatar_url) return null;
    const {data}=supabase.storage.from('avatars').getPublicUrl(p.avatar_url);
    return data?.publicUrl||null;
  }

  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="search-page">
          <h2 style={{fontFamily:T.displayFont,marginBottom:8}}>{lang==='ar'?'🔍 ابحث عن ناس':'🔍 Find People'}</h2>
          <p style={{color:'var(--text-muted)',fontSize:'0.87rem',marginBottom:24}}>{lang==='ar'?'ابحث باسم المستخدم أو الاسم وابعت لهم ضو':'Search by username or name and send them a glow'}</p>
          <div className="search-input-wrap">
            <span className="search-input-icon">🔍</span>
            <input type="text" className="search-input" placeholder={lang==='ar'?'ابحث بالاسم أو @username…':'Search by name or @username…'} value={query} onChange={e=>setQuery(e.target.value)} autoFocus style={{textAlign:T.dir==='rtl'?'right':'left'}}/>
            {query&&<button className="inbox-search-clear" onClick={()=>setQuery('')}>✕</button>}
          </div>
          {loading&&<div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>✨</div>}
          {!loading&&searched&&results.length===0&&<div className="empty" style={{paddingTop:40}}><div className="empty-icon">👤</div><div className="empty-title">{lang==='ar'?'مش لاقي حد':'No one found'}</div></div>}
          {results.length>0&&(
            <div className="search-results">
              {results.map(p=>{
                const avatarUrl=getAvatarUrl(p);
                return (
                  <div key={p.id} className="search-result-card">
                    <div className="search-result-avatar">
                      {avatarUrl?<img src={avatarUrl} alt={p.name}/>:(p.avatar_emoji||'✨')}
                    </div>
                    <div className="search-result-info">
                      <div className="search-result-name" style={{fontFamily:T.displayFont}}>{p.name}</div>
                      {p.username&&<div className="search-result-username">@{p.username}</div>}
                      {p.bio&&<div className="search-result-bio">{p.bio}</div>}
                    </div>
                    <div className="search-result-actions">
                      {p.username&&<button className="btn btn-ghost" style={{fontSize:'0.78rem',padding:'7px 12px'}} onClick={()=>navigate(`/u/${p.username}`)}>{lang==='ar'?'بروفايل':'Profile'}</button>}
                      {user&&user.id!==p.id&&<button className="btn btn-glow" style={{fontSize:'0.78rem',padding:'7px 12px'}} onClick={()=>navigate('/send/'+p.jar_id)}>✨ {lang==='ar'?'ابعت ضو':'Send Glow'}</button>}
                      {user&&user.id===p.id&&<span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{lang==='ar'?'(أنت)':'(you)'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── RESET PASSWORD ───────────────────────────────────────────────────────────
function ResetPasswordPage({lang, setLang, user, onLogout}) {
  const T=t[lang];const navigate=useNavigate();
  const [password,setPassword]=useState('');const [confirm,setConfirm]=useState('');
  const [loading,setLoading]=useState(false);const [error,setError]=useState('');
  const [success,setSuccess]=useState(false);const [validSession,setValidSession]=useState(false);const [checking,setChecking]=useState(true);
  useEffect(()=>{
    const {data:{subscription}}=supabase.auth.onAuthStateChange(async(event)=>{if(event==='PASSWORD_RECOVERY')setValidSession(true);setChecking(false);});
    supabase.auth.getSession().then(({data:{session}})=>{if(session)setValidSession(true);setChecking(false);});
    return()=>subscription.unsubscribe();
  },[]);
  async function handleReset(e){
    e.preventDefault();setError('');
    if(password.length<8){setError(T.weakPassword);return;}
    if(password!==confirm){setError(lang==='ar'?'كلمتا السر مش متطابقتين':'Passwords do not match');return;}
    setLoading(true);
    const{error:err}=await supabase.auth.updateUser({password});
    if(err)setError(T.error);else{setSuccess(true);setTimeout(()=>navigate('/inbox'),3000);}
    setLoading(false);
  }
  if(checking) return (<><Particles/><div className="page" dir={T.dir}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">✨</div></div></div></>);
  if(success) return (<><Particles/><div className="page" dir={T.dir}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div className="center-icon">🎉</div><div className="center-title">{lang==='ar'?'اتغيرت كلمة السر!':'Password updated!'}</div></div></div></>);
  if(!validSession) return (<><Particles/><div className="page" dir={T.dir}><Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/><div className="center-page"><div style={{fontSize:'3rem',marginBottom:20}}>⏰</div><div className="center-title">{lang==='ar'?'اللينك انتهى':'Link expired'}</div><br/><br/><button className="btn btn-glow" onClick={()=>navigate('/login')}>{lang==='ar'?'اطلب لينك جديد':'Request new link'}</button></div></div></>);
  return (
    <>
      <Particles/>
      <div className="page" dir={T.dir} style={{fontFamily:T.font}}>
        <Nav lang={lang} setLang={setLang} user={user} onLogout={onLogout}/>
        <div className="auth-page"><div className="auth-card">
          <div className="auth-logo"><LogoIcon size={52}/></div>
          <div className="auth-title" style={{fontFamily:T.displayFont,textAlign:'center'}}>{lang==='ar'?'كلمة سر جديدة 🔐':'New password 🔐'}</div>
          <form onSubmit={handleReset}>
            <div className="form-group"><label className="form-label">{lang==='ar'?'كلمة السر الجديدة':'New password'}</label><input type="password" placeholder={T.passwordPlaceholder} value={password} onChange={e=>setPassword(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div>
            <div className="form-group"><label className="form-label">{lang==='ar'?'تأكيد كلمة السر':'Confirm password'}</label><input type="password" placeholder={lang==='ar'?'اكتبها تاني':'Type it again'} value={confirm} onChange={e=>setConfirm(e.target.value)} required style={{textAlign:'left',direction:'ltr'}}/></div>
            {password.length>0&&(<div style={{marginBottom:14}}><div style={{display:'flex',gap:4,marginBottom:4}}>{[1,2,3,4].map(i=>(<div key={i} style={{flex:1,height:3,borderRadius:2,background:password.length>=i*3?(i<=1?'#FF7070':i<=2?'#FFCC80':i<=3?'#2DD4BF':'#FFD166'):'rgba(255,255,255,0.1)',transition:'background 0.3s'}}/>))}</div><div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{password.length<4?(lang==='ar'?'ضعيفة جداً':'Too weak'):password.length<7?(lang==='ar'?'ضعيفة':'Weak'):password.length<10?(lang==='ar'?'كويسة':'Good'):(lang==='ar'?'قوية 💪':'Strong 💪')}</div></div>)}
            {error&&<div className="form-error">{error}</div>}
            <button className="btn btn-glow btn-full" type="submit" disabled={loading} style={{marginTop:8}}>{loading?'…':(lang==='ar'?'حفظ كلمة السر الجديدة':'Save new password')}</button>
          </form>
        </div></div>
      </div>
    </>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [lang,setLang]=useState(()=>localStorage.getItem('mgm_lang')||'en');
  const [user,setUser]=useState(null);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    localStorage.setItem('mgm_lang',lang);
    document.documentElement.lang=lang;
    document.documentElement.dir=t[lang].dir;
  },[lang]);

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{setUser(session?.user||null);setLoading(false);});
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_,session)=>{setUser(session?.user||null);});
    return()=>subscription.unsubscribe();
  },[]);

  async function handleLogout(){await supabase.auth.signOut();setUser(null);}

  if(loading) return (<div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0D0A14',color:'#FFD166',fontSize:'1.5rem'}}>✨</div>);

  const props={lang,setLang,user,onLogout:handleLogout};
  return (
    <BrowserRouter>
      <CookieBanner lang={lang}/>
      <Routes>
        <Route path="/" element={<HomePage {...props}/>}/>
        <Route path="/signup" element={<SignupPage {...props}/>}/>
        <Route path="/login" element={<LoginPage {...props}/>}/>
        <Route path="/reset" element={<ResetPasswordPage {...props}/>}/>
        <Route path="/inbox" element={<InboxPage {...props}/>}/>
        <Route path="/profile" element={<MyProfilePage {...props}/>}/>
        <Route path="/search" element={<SearchPage {...props}/>}/>
        <Route path="/u/:username" element={<PublicProfilePage {...props}/>}/>
        <Route path="/send/:jarId" element={<SendPage {...props}/>}/>
        <Route path="/daily" element={<DailyJarPage {...props}/>}/>
        <Route path="/glow-wall" element={<GlowWallPage {...props}/>}/>
        <Route path="/privacy" element={<PrivacyPage lang={lang}/>}/>
        <Route path="/glow-mgmt-secure" element={<AdminPage user={user}/>}/>
        <Route path="/admin" element={<AdminPage user={user}/>}/>
        <Route path="/:jarId" element={<SendPage {...props}/>}/>
      </Routes>
    </BrowserRouter>
  );
}
